/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {dlsZIndex} from '@ion/dls/src/theme/theme';
import {getContainerHeight} from '@ion/dls/src/utils/ComponentUtils';
import IonSpinner, {IonSpinnerTypes} from './IonSpinner';

interface ContentLoaderTypes extends IonSpinnerTypes {
  fullPage?: boolean;
  marginLeft?: number;
  isContainerCentered?: boolean;
  containerHeight?: string;
}
type toggleStyleParams = string | number;

const toggleCenteredStyle = (manual: toggleStyleParams, auto: toggleStyleParams): any =>
  ({isContainerCentered}: ContentLoaderTypes) => isContainerCentered ? manual : auto;

const useStyles = makeStyles(theme => ({
  container : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    flexDirection  : 'column',
    textAlign      : 'center',
    width          : '100%',
    marginTop      : toggleCenteredStyle(0, -45),
    marginLeft     : ({marginLeft}: ContentLoaderTypes) => marginLeft,
    position       : toggleCenteredStyle('relative', 'absolute'),
    zIndex         : dlsZIndex('contentLoaderContainer'),
    top            : '0px',
    bottom         : '0px',
    height         : ({containerHeight}: ContentLoaderTypes) => containerHeight
  }
}));

/**
 * The ContentLoader provides a standard indeterminate loading indicator that
 * can be used to indicate a content based component (such as a page, an editor,
 * etc.) is in the process of initializing.
 *
 * ```typescript
 *
 * import React from 'react';
 * import {withTranslation} from 'react-i18next';
 * import {ContentLoader} from '@ion/dls/src/components/loading/ContentLoader';
 * import {LocalizedPageProps} from '@ion/applications/src/types/pages';
 *
 * export const Page = ({t, tReady}: LocalizedPageProps) => {
 *  return (
 *    <Layout>
 *      <Head>
 *        <title>{t('pages:page.title')}</title>
 *      </Head>
 *      {tReady ? <PageContent /> : <ContentLoader width='60px' height='60px' />}
 *    </Layout>
 *   );
 * };
 * export default withTranslation(['page', 'common'])(Page);
 *
 * ```
 *
 */
export const ContentLoader = ({
  width,
  height,
  fullPage,
  marginLeft= 0,
  isContainerCentered = false
}: ContentLoaderTypes) => {
  const containerHeight = getContainerHeight({isCentered: isContainerCentered, isFullPageDisplay: fullPage});
  const classes         = useStyles({fullPage, marginLeft, isContainerCentered, containerHeight});

  return (
    <div className={classes.container}>
      <IonSpinner width={width} height={height} />
    </div>
  );
};
